import type { ReadingDue } from "@/types";

/**
 * "No test needed yet", and why — the other half of the test reminder.
 *
 * The reminder used to say nothing at all until the cover ran out, which made
 * its silence unreadable. Silence was ambiguous between *there is cover left*,
 * *chlorine was superseded so the app has no opinion*, and *there is no CYA to
 * derive a floor from* — three quite different situations, one of which is
 * reassuring and two of which are not. An owner cannot tell "you are fine" from
 * "I cannot tell" by looking at nothing.
 *
 * **Deliberately quiet.** `OvernightCard` makes the argument this follows: a
 * green tick for "nothing is wrong" on a dashboard you check daily becomes
 * wallpaper, and wallpaper is what you stop reading right before the one day it
 * would have mattered. So this is grey text under the panel heading, not a card
 * and not a status colour.
 *
 * **It still shows its working.** A claim that the water needs nothing is a
 * claim about water people swim in, and it has to be as auditable as the
 * instruction is — the figures are the same measured ones, and it names which
 * of the two rates actually bound. What it never does, in either state, is
 * print a projected chlorine level (ADR 0014).
 *
 * Renders nothing when the cover is spent: `ReadingDueCard` has the floor then,
 * and the same fact stated in two places on one screen reads as two facts.
 */
export default function ReadingCoverLine({ due }: { due: ReadingDue }) {
  if (due.due) return null;

  return (
    <div className="mt-1.5">
      <p className="text-xs text-(--color-ink-muted)">{due.headline}</p>
      <p className="mt-0.5 text-xs text-(--color-ink-faint)">{due.note}</p>
    </div>
  );
}
